import { useState, useEffect } from 'react'
import { InfoTip } from '../../components/InfoTip'
import { useToast } from '../../context/ToastContext'
import { useAuth } from '../../context/AuthContext'
import { exportToExcel } from '../../utils/exportExcel'
import { MobileCard, MobileOnly, DesktopOnly } from '../../components/MobileCard'
import type { User } from '../../types'

import { API_BASE } from '../../utils/api'

interface UserFormData {
  displayName: string
  username: string
  password: string
  role: 'admin' | 'trader' | 'broker'
  selected_broker_id: string
  mapped_client_id: string
}

const emptyForm: UserFormData = { displayName: '', username: '', password: '', role: 'trader', selected_broker_id: '', mapped_client_id: '' }

export function AdminUsers() {
  const [users, setUsers] = useState<User[]>([])
  const [brokers, setBrokers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const { token, session } = useAuth()

  const fetchUsersAndBrokers = async () => {
    try {
      const [res, brokersRes] = await Promise.all([
        fetch(`${API_BASE}/api/users`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/api/brokers`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
      ])
      if (brokersRes.ok) {
        setBrokers(await brokersRes.json())
      }
      if (res.ok) {
        const data = await res.json()
        setUsers(data.map((u: any) => ({
          id: u.id,
          username: u.username,
          displayName: u.display_name || u.username,
          role: u.role,
          status: u.status,
          email: u.email,
          lastLogin: u.last_login,
        })))
      }
    } catch (err) {
      console.warn('Users API unavailable:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch users from API on mount
  useEffect(() => {
    fetchUsersAndBrokers()
  }, [])
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null)
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [formData, setFormData] = useState<UserFormData>(emptyForm)
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const { addToast } = useToast()

  const openCreate = () => {
    setFormData(emptyForm)
    setEditingUserId(null)
    setModalMode('create')
  }

  const openEdit = (user: User) => {
    // Find if this user has a mapped client
    let mappedClientId = ''
    let selectedBrokerId = ''
    for (const b of brokers) {
      const c = b.clients?.find((cl: any) => cl.mapped_user_id === user.id)
      if (c) {
        mappedClientId = c.id
        selectedBrokerId = b.id
      }
    }
    setFormData({ displayName: user.displayName, username: user.username, password: '', role: user.role, selected_broker_id: selectedBrokerId, mapped_client_id: mappedClientId })
    setEditingUserId(user.id)
    setModalMode('edit')
  }

  const closeModal = () => {
    setModalMode(null)
    setEditingUserId(null)
    setFormData(emptyForm)
  }

  const handleSave = async () => {
    if (!formData.displayName.trim() || !formData.username.trim()) {
      addToast('Display name and username are required', 'error')
      return
    }

    if (modalMode === 'create') {
      if (!formData.password.trim()) {
        addToast('Password is required for new users', 'error')
        return
      }
      if (users.some((u) => u.username === formData.username.trim())) {
        addToast('Username already exists', 'error')
        return
      }
      try {
        const res = await fetch(`${API_BASE}/api/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            username: formData.username.trim(),
            display_name: formData.displayName.trim(),
            role: formData.role,
            password: formData.password,
            email: `${formData.username.trim()}@lamoney.in`,
            mapped_client_id: formData.mapped_client_id || null
          }),
        })
        if (res.ok) {
          const result = await res.json()
          await fetchUsersAndBrokers() // RE-FETCH to ensure table updates perfectly
          addToast(`User "${formData.displayName.trim()}" created`, 'success')
          closeModal()
        } else {
          const errData = await res.json().catch(() => ({}))
          addToast(errData.error || 'Failed to create user', 'error')
        }
      } catch (err) {
        console.warn('Create user failed:', err)
        addToast('API unavailable', 'error')
      }
    } else if (modalMode === 'edit' && editingUserId) {
      try {
        const res = await fetch(`${API_BASE}/api/users/${editingUserId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            display_name: formData.displayName.trim(),
            role: formData.role,
            password: formData.password || undefined,
            mapped_client_id: formData.mapped_client_id || null
          }),
        })
        if (res.ok) {
          await fetchUsersAndBrokers()
          addToast('User updated', 'success')
          closeModal()
        } else {
          const errData = await res.json().catch(() => ({}))
          addToast(errData.error || 'Failed to update user', 'error')
        }
      } catch (err) {
        console.warn('Update user failed:', err)
      }
    }
    closeModal()
  }

  const toggleStatus = async (user: User) => {
    const newStatus = user.status === 'active' ? 'disabled' : 'active'
    // Optimistic update
    setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, status: newStatus } : u))
    addToast(
      `${user.displayName} ${newStatus === 'active' ? 'enabled' : 'disabled'}`,
      newStatus === 'active' ? 'success' : 'warning'
    )
    try {
      await fetch(`${API_BASE}/api/users/${user.id}/toggle-status`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      })
    } catch (err) {
      console.warn('Toggle status failed:', err)
    }
  }

  const deleteUser = async (user: User) => {
    if (session?.user?.id === user.id) {
      addToast('Cannot delete your own account', 'error')
      return
    }
    setDeleteTarget(user)
    setDeleteConfirmText('')
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      const res = await fetch(`${API_BASE}/api/users/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (res.ok) {
        setUsers(prev => prev.filter(u => u.id !== deleteTarget.id))
        addToast(`User "${deleteTarget.displayName}" deleted permanently`, 'success')
      } else {
        const errData = await res.json().catch(() => ({}))
        addToast(errData.error || 'Failed to delete user', 'error')
      }
    } catch (err) {
      console.warn('Delete user failed:', err)
      addToast('API unavailable', 'error')
    } finally {
      setDeleteTarget(null)
      setDeleteConfirmText('')
    }
  }

  return (
    <div>
      <div className="page-head">
        <h1>Users <InfoTip term="userManagement" placement="bottom" /></h1>
        <p className="page-sub">Manage trader and admin accounts</p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button className="btn btn-primary" onClick={openCreate}>
            + Add User
          </button>
          <button className="btn btn-sm btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
            onClick={() => {
              exportToExcel(users.map(u => ({
                Name: u.displayName,
                Username: u.username,
                Role: u.role,
                Status: u.status,
                LastLogin: u.lastLogin || '',
              })), `LaMoney_Users_${new Date().toISOString().split('T')[0]}`)
              addToast('Excel exported successfully', 'success')
            }}>
            ⬇ Export Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>⏳ Loading users…</span>
        </div>
      ) : (
      <>
      <DesktopOnly>
      <div className="card">
        <table>
          <thead>
            <tr><th>Name</th><th>Username</th><th>Mapped Client</th><th>Role</th><th>Status</th><th>Last Login</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const mappedClient = brokers.flatMap(b => b.clients || []).find((c: any) => c.mapped_user_id === u.id)
              const mappedBroker = mappedClient ? brokers.find(b => b.id === mappedClient.broker_id) : null
              return (
              <tr key={u.id}>
                <td>
                  <div className="user-cell">
                    <div className="avatar sm">{u.displayName.charAt(0)}</div>
                    {u.displayName}
                  </div>
                </td>
                <td><code>{u.username}</code></td>
                <td>
                  {mappedClient && mappedBroker ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span className="font-mono text-sm" style={{ color: 'var(--text-main)' }}>{mappedClient.client_id}</span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{mappedBroker.name}</span>
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem', fontStyle: 'italic' }}>—</span>
                  )}
                </td>
                <td>
                  <span className={`badge ${u.role === 'admin' ? 'badge-target' : 'badge-sl'}`}>
                    {u.role}
                  </span>
                </td>
                <td>
                  <span className={`badge ${u.status === 'active' ? 'badge-entry' : 'badge-sl'}`}>
                    {u.status}
                  </span>
                </td>
                <td style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{u.lastLogin || '—'}</td>
                <td>
                  <button className="btn btn-sm btn-ghost" onClick={() => openEdit(u)}>Edit</button>
                  <button
                    className="btn btn-sm btn-ghost"
                    style={{ marginLeft: '0.25rem', color: u.status === 'active' ? 'var(--red)' : 'var(--green)' }}
                    onClick={() => toggleStatus(u)}
                  >
                    {u.status === 'active' ? 'Disable' : 'Enable'}
                  </button>
                  {session?.user?.id !== u.id && (
                    <button
                      className="btn btn-sm btn-ghost"
                      style={{ marginLeft: '0.25rem', color: 'var(--red)' }}
                      onClick={() => deleteUser(u)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>
      </DesktopOnly>

      <MobileOnly>
        <div className="m-card-list">
          {users.map((u) => (
            <MobileCard
              key={u.id}
              title={u.displayName}
              subtitle={u.username}
              status={{
                label: u.status === 'active' ? 'Active' : 'Disabled',
                variant: u.status === 'active' ? 'green' : 'red',
              }}
              fields={[
                { label: 'Role', value: u.role, bold: true },
                { label: 'Email', value: u.email || '—', accent: 'muted' },
              ]}
              actions={[
                { label: 'Edit', icon: '✏️', variant: 'ghost', onClick: () => openEdit(u) },
                {
                  label: u.status === 'active' ? 'Disable' : 'Enable',
                  icon: u.status === 'active' ? '🚫' : '✅',
                  variant: u.status === 'active' ? 'danger' : 'primary',
                  onClick: () => toggleStatus(u),
                },
              ]}
            />
          ))}
        </div>
      </MobileOnly>
      </>
      )}

      {modalMode && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'create' ? 'Add User' : 'Edit User'}</h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="cfg-grid">
                <div className="cfg-field">
                  <label className="cfg-label">Display Name</label>
                  <input className="cfg-input" placeholder="e.g. Trader Delta"
                    value={formData.displayName}
                    onChange={(e) => setFormData((p) => ({ ...p, displayName: e.target.value }))} />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Username</label>
                  <input className="cfg-input" placeholder="e.g. trader4"
                    value={formData.username}
                    onChange={(e) => setFormData((p) => ({ ...p, username: e.target.value }))} />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Password {modalMode === 'edit' && <span className="cfg-hint">(leave blank to keep)</span>}</label>
                  <input className="cfg-input" type="password" placeholder="••••••"
                    value={formData.password}
                    onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))} />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Role <InfoTip term="mode" /></label>
                  <select className="cfg-input" value={formData.role}
                    onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value as any }))}>
                    <option value="trader">Trader</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {formData.role === 'trader' && (
                  <>
                    <div className="cfg-field">
                      <label className="cfg-label">Select Broker Firm</label>
                      <select className="cfg-input" value={formData.selected_broker_id}
                        onChange={(e) => setFormData((p) => ({ ...p, selected_broker_id: e.target.value, mapped_client_id: '' }))}>
                        <option value="">-- No Broker Selected --</option>
                        {brokers.map(b => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                    {formData.selected_broker_id && (
                      <div className="cfg-field">
                        <label className="cfg-label">Map Client ID to this Login</label>
                        <select className="cfg-input" value={formData.mapped_client_id}
                          onChange={(e) => setFormData((p) => ({ ...p, mapped_client_id: e.target.value }))}>
                          <option value="">-- No Client Mapped --</option>
                          {brokers.find(b => b.id === formData.selected_broker_id)?.clients?.map((c: any) => {
                            const isMapped = c.mapped_user_id && c.mapped_user_id !== editingUserId
                            return (
                              <option key={c.id} value={c.id} disabled={isMapped}>
                                {c.client_id} {isMapped ? '(Already mapped to another user)' : ''}
                              </option>
                            )
                          })}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {modalMode === 'create' ? 'Create' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2 style={{ color: 'var(--red)', margin: 0 }}>⚠ Delete User</h2>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                You are about to <strong>permanently delete</strong> the following user. This action cannot be undone.
              </p>
              <div className="card" style={{ padding: '0.75rem', margin: 0 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>{deleteTarget.displayName}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                  @{deleteTarget.username} • {deleteTarget.role} • ID: <code style={{ fontFamily: 'monospace', fontSize: '0.6875rem', background: 'var(--bg-elevated)', padding: '1px 4px', borderRadius: '3px' }}>{deleteTarget.id}</code>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>
                  Type the username <strong style={{ color: 'var(--text-primary)' }}>"{deleteTarget.username}"</strong> to confirm:
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder={deleteTarget.username}
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  autoFocus
                  style={{ fontSize: '0.8125rem' }}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ gap: '0.5rem' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                disabled={deleteConfirmText !== deleteTarget.username}
                onClick={confirmDelete}
                style={{ opacity: deleteConfirmText === deleteTarget.username ? 1 : 0.4 }}
              >
                🗑 Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
